<template>
  <form class="authBar bar" novalidate @submit.prevent="doLogin">
    <h1 class="token-text-h4 mb-6" data-test="sign-in-title">
      {{ application ? `Sign in to continue to ${application}` : 'Sign in' }}
    </h1>
    <label class="token-text-regular" for="login-email">Email</label>
    <CustomTextInput
      class="mt-2"
      v-model="form.login"
      input-id="login-email"
      name="email"
      autocomplete="username"
      placeholder="hello@email.com"
    />
    <label class="token-text-regular mt-2" for="login-password">Password</label>
    <CustomTextInput
      class="mt-2"
      v-model="form.password"
      input-id="login-password"
      name="password"
      autocomplete="current-password"
      placeholder="••••••••"
      type="password"
    />
    <!-- Generic Error -->
    <div class="mt-2" v-if="errors">
      <Message :severity="Severity.DANGER" :title="'Error'" :text="errors" />
    </div>
    <CustomButton
      type="submit"
      :level="ButtonLevels.Primary"
      class="cta mt-2"
      :status="buttonState"
      :text="btnText"
      :disabled="loading"
      :loading="loading"
    />

    <TextBetweenLines class="my-4">
      <span
        class="token-text-color-secondary token-text-microbold token-text-transform-uppercase token-text-font-iAWriterDuoV"
      >
        or
      </span>
    </TextBetweenLines>

    <CustomButton
      :level="ButtonLevels.Secondary"
      @click="routeur.push({ name: 'register', query: carriedReturnTo(route.query) })"
      class="cta mt-2"
      text="Create an account"
    />

    <CustomButton
      :level="ButtonLevels.Tertiary"
      @click="routeur.push({ name: 'forgot-password', query: carriedReturnTo(route.query) })"
      class="cta mt-2"
      text="Forgot your password?"
    />
    <CustomButton
      v-if="application"
      data-test="cancel-application"
      :level="ButtonLevels.Tertiary"
      class="cta mt-2"
      :text="`Cancel and go back to ${application}`"
      :disabled="cancelling"
      :loading="cancelling"
      @click="cancel"
    />
  </form>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import CustomButton from '@shared/components/UIElements/CustomButton.vue';
import CustomTextInput from '@shared/components/UIElements/CustomTextInput.vue';
import TextBetweenLines from '@shared/components/UIElements/TextBetweenLines.vue';
import { useRoute, useRouter } from 'vue-router';
import { ButtonLevels, Severity } from '@shared/enums';
import sessionService from '@/services/sessionService';
import { redirectIfConnected } from '@/utils/redirect';
import { carriedReturnTo } from '@/utils/returnTo';
import { useApplicationSignIn } from '@/utils/applicationSignIn';
import Message from '@/shared/components/UIElements/Message.vue';

const routeur = useRouter();
const route = useRoute();
// An application's sign-in returns here through the portal's own sign-in page.
const { application, cancel, cancelling } = useApplicationSignIn();

const form = ref({
  login: '',
  password: '',
});
const loading = ref(false);
const btnText = ref('Sign in');

const buttonState = ref(undefined);
const errors = ref(null);

const doLogin = () => {
  loading.value = true;
  errors.value = null;

  sessionService
    .login(form.value.login, form.value.password)
    .then(() => {
      redirectIfConnected()
        .catch(() => {
          console.error('Error during redirection after login');
        })
        .finally(() => {
          loading.value = false;
        });
    })
    .catch((error) => {
      errors.value =
        error.response?.data?.errors?.[0]?.detail || 'You could not be signed in. Try again.';
      loading.value = false;
    });
};
</script>
